import { z } from "zod";

export const PHONE_NUMBER_MESSAGE_CLASSES = [
  "SHORT_CODE",
  "TEN_DLC",
  "TOLL_FREE",
  "UNREGISTERED",
] as const;
export type PhoneNumberMessageClassValue =
  (typeof PHONE_NUMBER_MESSAGE_CLASSES)[number];

export const PROVIDER_NAMES = ["BANDWIDTH", "FAKE", "SINCH", "TWILIO"] as const;
export type ProviderNameValue = (typeof PROVIDER_NAMES)[number];

export const SHORT_CODE_TYPES = ["REGULAR", "VANITY"] as const;
export type ShortCodeTypeValue = (typeof SHORT_CODE_TYPES)[number];

export const PHONE_NUMBER_STATES_FOR_SHORT_CODE = ["NEW", "ACTIVE"] as const;

export const SHORT_CODE_OR_E164_REGEX = /^(\d{5,6}|\+1\d{10})$/;
export const E164_US_REGEX = /^\+1\d{10}$/;
export const TCR_CAMPAIGN_ID_REGEX = /^(C|c)(\w|\d){6}$/;

export function validatePhoneNumberForMessageClass(
  ctx: z.RefinementCtx,
  {
    phoneNumber,
    messageClass,
    path = ["phoneNumber"],
  }: {
    phoneNumber: string;
    messageClass: PhoneNumberMessageClassValue | undefined;
    path?: (string | number)[];
  }
): void {
  if (messageClass === "SHORT_CODE") {
    if (!SHORT_CODE_OR_E164_REGEX.test(phoneNumber)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Must be a valid 5-digit or 6-digit short code or an E.164-formatted US phone number",
        path,
      });
    }
  } else if (messageClass) {
    if (!E164_US_REGEX.test(phoneNumber)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Must be a valid E.164-formatted US phone number",
        path,
      });
    }
  }
}

export function validateShortCodeTypeForMessageClass(
  ctx: z.RefinementCtx,
  {
    shortCodeType,
    messageClass,
    path = ["shortCodeType"],
    requiredMessage = "Short code type is required when message class is Short Code",
  }: {
    shortCodeType: ShortCodeTypeValue | string | null | undefined;
    messageClass: PhoneNumberMessageClassValue | undefined;
    path?: (string | number)[];
    requiredMessage?: string;
  }
): void {
  if (messageClass === "SHORT_CODE") {
    if (!shortCodeType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path,
      });
    }
  } else if (shortCodeType) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        "Short code type can only be set for phone numbers with a short code message class",
      path,
    });
  }
}

const requiredString = z.string().min(1, "Required");
const requiredStringList = z.array(requiredString).min(1, "Required");

// Backend GraphQL-input shape: keywords are string arrays.
export const shortCodeConfigInputZodSchema = z.object({
  optInKeywords: requiredStringList,
  optInMessage: requiredString,
  optOutKeywords: requiredStringList,
  optOutMessage: requiredString,
  helpKeywords: requiredStringList,
  helpMessage: requiredString,
});

export const importOwnedPhoneNumberInputZodSchema = z
  .object({
    phoneNumber: z.string(),
    messageClass: z.enum(PHONE_NUMBER_MESSAGE_CLASSES),
    provider: z.enum(PROVIDER_NAMES),
    shortCodeConfig: shortCodeConfigInputZodSchema.nullish(),
    tcrCampaignId: z.string().nullish(),
    state: z.string().nullish(),
    shortCodeType: z.enum(SHORT_CODE_TYPES).nullish(),
    notifyTollFreeCreated: z.boolean().nullish(),
  })
  .superRefine((data, ctx) => {
    validatePhoneNumberForMessageClass(ctx, {
      phoneNumber: data.phoneNumber,
      messageClass: data.messageClass,
    });

    if (data.messageClass === "SHORT_CODE" && !data.state) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "State is required for short code imports",
        path: ["state"],
      });
    }

    validateShortCodeTypeForMessageClass(ctx, {
      shortCodeType: data.shortCodeType,
      messageClass: data.messageClass,
    });

    if (data.tcrCampaignId && !TCR_CAMPAIGN_ID_REGEX.test(data.tcrCampaignId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Must be a valid TCR campaign ID (7-character string starting with C)",
        path: ["tcrCampaignId"],
      });
    }

    const notifyTollFreeCreatedAllowed =
      data.messageClass === "TOLL_FREE" &&
      (data.provider === "BANDWIDTH" || data.provider === "FAKE");
    if (data.notifyTollFreeCreated && !notifyTollFreeCreatedAllowed) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Notifications can only occur for toll-free numbers with Bandwidth",
        path: ["notifyTollFreeCreated"],
      });
    }
  });

export const editOwnedPhoneNumberInputZodSchema = z.object({
  shortCodeConfig: shortCodeConfigInputZodSchema.nullish(),
  shortCodeType: z.enum(SHORT_CODE_TYPES).nullish(),
});

// Frontend form shape: keywords are comma-separated strings.
export const ShortCodeConfigSchema = z.object({
  optInKeywords: z.string(),
  optInMessage: z.string(),
  optOutKeywords: z.string(),
  optOutMessage: z.string(),
  helpKeywords: z.string(),
  helpMessage: z.string(),
});

export const SHORT_CODE_CONFIG_KEYS = Object.keys(
  ShortCodeConfigSchema.shape
) as (keyof z.infer<typeof ShortCodeConfigSchema>)[];

export const EditOwnedPhoneNumberFormBaseSchema = z.object({
  displayName: z.string(),
  tcrCampaignId: z.string(),
  shortCodeConfig: ShortCodeConfigSchema,
  state: z.string(),
  shortCodeType: z.string(),
});

export function makeEditOwnedPhoneNumberFormSchema({
  isStaff,
  messageClass,
  hasExistingTcrCampaignId,
  hasShortCodeConfig,
}: {
  isStaff: boolean;
  messageClass: PhoneNumberMessageClassValue;
  hasExistingTcrCampaignId: boolean;
  hasShortCodeConfig: boolean;
}): z.ZodEffects<typeof EditOwnedPhoneNumberFormBaseSchema> {
  return EditOwnedPhoneNumberFormBaseSchema.superRefine((data, ctx) => {
    if (!isStaff) {
      return;
    }

    if (
      messageClass === "TEN_DLC" &&
      hasExistingTcrCampaignId &&
      !data.tcrCampaignId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Cannot delete TCR Campaign ID",
        path: ["tcrCampaignId"],
      });
    }
    if (data.tcrCampaignId && !TCR_CAMPAIGN_ID_REGEX.test(data.tcrCampaignId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid TCR Campaign ID",
        path: ["tcrCampaignId"],
      });
    }

    if (hasShortCodeConfig) {
      for (const shortCodeConfigKey of SHORT_CODE_CONFIG_KEYS) {
        if (!data.shortCodeConfig[shortCodeConfigKey]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
            path: ["shortCodeConfig", shortCodeConfigKey],
          });
        }
      }
    }

    if (messageClass === "SHORT_CODE") {
      if (
        !(PHONE_NUMBER_STATES_FOR_SHORT_CODE as readonly string[]).includes(
          data.state
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Status must be Pending or Active",
          path: ["state"],
        });
      }
      if (
        !(SHORT_CODE_TYPES as readonly string[]).includes(data.shortCodeType)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Short code type is required",
          path: ["shortCodeType"],
        });
      }
    }
  });
}

export const ImportOwnedPhoneNumberFormSchema = z
  .object({
    phoneNumber: z.string().min(1, "Required"),
    messageClass: z.enum(PHONE_NUMBER_MESSAGE_CLASSES),
    provider: z.enum(PROVIDER_NAMES),
    twilioAccountSid: z.string().optional(),
    ownedPhoneNumberPoolId: z.string().optional(),
    isOptInNumber: z.boolean(),
    shortCodeConfig: ShortCodeConfigSchema,
    tcrCampaignId: z
      .union([
        z.string().regex(TCR_CAMPAIGN_ID_REGEX, "Invalid 10DLC Campaign ID"),
        z.literal(""),
      ])
      .optional(),
    state: z.string().optional(),
    shortCodeType: z.string().optional(),
    allowRiskyMove: z.boolean(),
    notifyTollFreeCreated: z.boolean(),
  })
  .superRefine((data, ctx) => {
    validatePhoneNumberForMessageClass(ctx, {
      phoneNumber: data.phoneNumber,
      messageClass: data.messageClass,
    });

    if (data.messageClass === "SHORT_CODE") {
      if (!data.state) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "State is required for short code imports",
          path: ["state"],
        });
      }
      validateShortCodeTypeForMessageClass(ctx, {
        shortCodeType: data.shortCodeType,
        messageClass: data.messageClass,
        requiredMessage: "Short code type is required for short code imports",
      });
    }

    if (data.isOptInNumber) {
      for (const shortCodeConfigKey of SHORT_CODE_CONFIG_KEYS) {
        if (!data.shortCodeConfig[shortCodeConfigKey]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
            path: ["shortCodeConfig", shortCodeConfigKey],
          });
        }
      }
    }
  });
